import React, { useState } from 'react';
import { Plus, Clock, Calendar, Edit, Trash2, Play, Pause } from 'lucide-react';
import { PageTracker } from '../../../components/PageTracker.tsx';

const TimeBlocking: React.FC = () => {
  const [timeBlocks, setTimeBlocks] = useState([
    {
      id: 1,
      title: "Morning Routine",
      description: "Exercise, meditation, and breakfast",
      startTime: "06:00",
      endTime: "08:00",
      category: "Personal",
      color: "bg-green-500",
      day: "today",
      status: "completed"
    },
    {
      id: 2,
      title: "Deep Work - React Project",
      description: "Focus time for developing personal portfolio",
      startTime: "09:00",
      endTime: "11:30",
      category: "Work",
      color: "bg-blue-500",
      day: "today",
      status: "active"
    },
    {
      id: 3,
      title: "Learning Session",
      description: "Study advanced TypeScript concepts",
      startTime: "14:00",
      endTime: "15:30",
      category: "Learning",
      color: "bg-purple-500",
      day: "today",
      status: "upcoming"
    },
    {
      id: 4,
      title: "Personal Projects",
      description: "Work on side projects and experiments",
      startTime: "19:00",
      endTime: "21:00",
      category: "Projects",
      color: "bg-orange-500",
      day: "today",
      status: "upcoming"
    }
  ]);

  const [currentTime] = useState(new Date().toLocaleTimeString('en-US', { 
    hour12: false, 
    hour: '2-digit', 
    minute: '2-digit' 
  }));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'border-green-500 bg-green-50';
      case 'active': return 'border-blue-500 bg-blue-50 ring-2 ring-blue-200';
      case 'upcoming': return 'border-gray-300 bg-white';
      default: return 'border-gray-300 bg-white';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <Calendar className="text-green-600" size={16} />;
      case 'active': return <Play className="text-blue-600" size={16} />;
      case 'upcoming': return <Clock className="text-gray-600" size={16} />;
      default: return <Clock className="text-gray-600" size={16} />;
    }
  };

  const calculateDuration = (startTime: string, endTime: string) => {
    const start = new Date(`2025-01-01T${startTime}:00`);
    const end = new Date(`2025-01-01T${endTime}:00`);
    const diffMs = end.getTime() - start.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto project-mgmt-page">
      <PageTracker pageTitle="Time Blocking" category="project-management" />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Time Blocking</h1>
        <p className="text-gray-600">
          Plan your day with intentional time blocks. Allocate specific time periods for different activities, 
          maintain focus, and ensure important tasks get dedicated attention in your schedule.
        </p>
      </div>

      <div className="mb-6 flex justify-between items-center">
        <div className="flex space-x-4 items-center">
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700">
            <Plus size={20} />
            <span>Add Time Block</span>
          </button>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Clock size={16} />
            <span>Current time: {currentTime}</span>
          </div>
        </div>
        <div className="flex space-x-2">
          <button className="border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50">
            Today
          </button>
          <button className="border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50">
            Tomorrow
          </button>
          <button className="border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50">
            This Week
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Today's Schedule</h2>
            
            <div className="space-y-4">
              {timeBlocks
                .sort((a, b) => a.startTime.localeCompare(b.startTime))
                .map((block) => (
                  <div 
                    key={block.id} 
                    className={`border-l-4 ${block.color} rounded-lg border ${getStatusColor(block.status)} p-4 transition-all hover:shadow-md`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(block.status)}
                        <h3 className="font-semibold text-gray-900">{block.title}</h3>
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                          {block.category}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="text-gray-400 hover:text-blue-600">
                          <Edit size={16} />
                        </button>
                        <button className="text-gray-400 hover:text-red-600">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-3">{block.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1">
                          <Clock size={14} className="text-gray-500" />
                          <span className="text-gray-700">
                            {block.startTime} - {block.endTime}
                          </span>
                        </div>
                        <span className="text-gray-500">
                          ({calculateDuration(block.startTime, block.endTime)}h)
                        </span>
                      </div>
                      
                      {block.status === 'active' && (
                        <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 flex items-center space-x-1">
                          <Pause size={14} />
                          <span>Pause</span>
                        </button>
                      )}
                      
                      {block.status === 'upcoming' && (
                        <button className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 flex items-center space-x-1">
                          <Play size={14} />
                          <span>Start</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
        
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Blocks:</span>
                <span className="font-medium">{timeBlocks.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Completed:</span>
                <span className="font-medium text-green-600">
                  {timeBlocks.filter(b => b.status === 'completed').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Active:</span>
                <span className="font-medium text-blue-600">
                  {timeBlocks.filter(b => b.status === 'active').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Upcoming:</span>
                <span className="font-medium text-gray-600">
                  {timeBlocks.filter(b => b.status === 'upcoming').length}
                </span>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Time Distribution</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Work:</span>
                <span className="font-medium">
                  {timeBlocks
                    .filter(b => b.category === 'Work')
                    .reduce((acc, b) => acc + calculateDuration(b.startTime, b.endTime), 0)
                    .toFixed(1)}h
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Learning:</span>
                <span className="font-medium">
                  {timeBlocks
                    .filter(b => b.category === 'Learning')
                    .reduce((acc, b) => acc + calculateDuration(b.startTime, b.endTime), 0)
                    .toFixed(1)}h
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Personal:</span>
                <span className="font-medium">
                  {timeBlocks
                    .filter(b => b.category === 'Personal')
                    .reduce((acc, b) => acc + calculateDuration(b.startTime, b.endTime), 0)
                    .toFixed(1)}h
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Projects:</span>
                <span className="font-medium">
                  {timeBlocks
                    .filter(b => b.category === 'Projects')
                    .reduce((acc, b) => acc + calculateDuration(b.startTime, b.endTime), 0)
                    .toFixed(1)}h
                </span>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Pro Tip</h3>
            <p className="text-blue-800 text-sm">
              Keep time blocks realistic and include buffer time between activities. 
              This helps maintain flexibility while staying organized.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeBlocking;
